// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import dayjs from 'dayjs';
import { Map, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';

import { Driverlocations, Moves } from '@gql/base-types';

import {
  Marker,
  Point,
  getDefaultBounds,
  getFilteredPoints,
  getSmoothPoints,
} from '@components/DriverTracker/driverTrackerService';
import {
  GoogleMapsElement,
  MapControls,
  MapNavigation,
  useMapPreferences,
  useMapRoute,
  useMapSurfaceColors,
} from '@components/Maps';

import { css } from '@emotion/css';
import { Theme, useTheme, Typography } from '@mui/material';

import DriverTrackerMarker from '@components/DriverTracker/DriverTrackerMarker';

// TYPES ---------------------------------------------------------------- //

interface DriverTrackerMapProps {
  move?: Moves;
  driverLocations?: Driverlocations[];
  markers?: Marker[];
}

// COMPONENT ---------------------------------------------------------------- //

const DriverTrackerMap = ({ move, driverLocations, markers }: DriverTrackerMapProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const { mapStyles: activeMapStyles } = useMapPreferences();
  const { primary: mapTextColor } = useMapSurfaceColors();

  const map = useMap(`driver-tracker-map`);
  const googleMapsService = useMapsLibrary(`maps`);

  const driverPathRenderedRef = React.useRef(false);

  const [etaTime, setEtaTime] = React.useState<string | undefined>(undefined);
  const [etaMinutes, setEtaMinutes] = React.useState<number | undefined>(undefined);

  // Compute origin/destination for the driver's current route.
  const driverMarker = markers?.find(m => m?.id === `driver`);
  const pickupMarker = markers?.find(m => m?.id === `pickup`);
  const deliveryMarker = markers?.find(m => m?.id === `delivery`);

  let routeOrigin: { lat: number; lng: number } | null = null;
  let routeDestination: { lat: number; lng: number } | null = null;
  if (driverMarker?.position?.lat && driverMarker?.position?.lng) {
    const originPos = { lat: driverMarker.position.lat, lng: driverMarker.position.lng };
    if (move?.status === `pickup started` && pickupMarker?.position?.lat && pickupMarker?.position?.lng) {
      routeOrigin = originPos;
      routeDestination = { lat: pickupMarker.position.lat, lng: pickupMarker.position.lng };
    } else if (move?.status === `delivery started` && deliveryMarker?.position?.lat && deliveryMarker?.position?.lng) {
      routeOrigin = originPos;
      routeDestination = { lat: deliveryMarker.position.lat, lng: deliveryMarker.position.lng };
    }
  }

  useMapRoute({
    mapId: 'driver-tracker-map',
    pickup: routeOrigin,
    delivery: routeDestination,
    enabled: !!routeOrigin && !!routeDestination,
    fitBounds: false,
    onRouteComputed: ({ durationSec }) => {
      setEtaMinutes(Math.round(durationSec / 60));
      setEtaTime(dayjs().add(durationSec, `seconds`).format(`h:mm A`));
    },
  });

  // Render the driver's historical breadcrumb path once we have driverLocations.
  React.useEffect(() => {
    if (driverPathRenderedRef.current) return;
    if (!map || !googleMapsService || !driverLocations?.length) return;

    driverPathRenderedRef.current = true;

    const renderDriverPath = async () => {
      try {
        const apiKey = import.meta.env.VITE_GOOGLE_API_KEY;

        const points: (Point | undefined)[] = driverLocations.map(dl => {
          if (dl?.location?.coordinates?.length) {
            return {
              lat: dl.location.coordinates[1],
              lng: dl.location.coordinates[0],
            } as Point;
          }
          return undefined;
        });

        const filteredPoints = await getFilteredPoints(points);
        const smoothPoints = await getSmoothPoints(filteredPoints, apiKey);

        const driverPath = new googleMapsService.Polyline({
          path: smoothPoints?.length ? smoothPoints : filteredPoints,
          geodesic: true,
          strokeColor: theme?.palette?.primary?.main,
          strokeOpacity: move?.status === `delivery successful` ? 1 : 0.5,
          strokeWeight: 8,
        });

        driverPath.setMap(map);
      } catch (err) {
        console.error(`Failed to render driver path:`, err);
      }
    };

    renderDriverPath();
  }, [map, googleMapsService, driverLocations, move?.status, theme?.palette?.primary?.main]);

  return (
    <div className={cls.mapContainer}>
      {/* ROUTE INFO */}
      {etaTime && etaMinutes ? (
        <GoogleMapsElement style={{ top: 10, left: 10 }}>
          <Typography sx={{ color: mapTextColor }}>
            ETA {etaTime} ({etaMinutes} min)
          </Typography>
        </GoogleMapsElement>
      ) : null}

      {/* MAP */}
      <Map
        id={`driver-tracker-map`}
        defaultBounds={getDefaultBounds(markers)}
        styles={activeMapStyles}
        clickableIcons={false}
        gestureHandling={`cooperative`}
        disableDefaultUI
      >
        {markers?.map((marker, i) => (
          <DriverTrackerMarker key={`driver-tracker-marker-${i}`} marker={marker} />
        ))}
      </Map>

      {/* Shared map controls (fullscreen + light/dark + focus) */}
      <MapControls />
      <MapNavigation mapId='driver-tracker-map' />
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => ({
  mapContainer: css`
    z-index: 1;
    overflow: hidden;
    position: relative;
    display: block;
    width: 100%;
    height: 720px;
    border: 1px solid ${theme?.palette?.action?.focus};
    border-radius: 10px;
    background-color: ${theme?.palette?.background?.paper};
    box-shadow: 0px 2px 12px 2px ${theme?.palette?.divider};
    cursor: pointer;
    ${theme?.breakpoints?.down('sm')} {
      height: 540px;
    }
    ${theme?.breakpoints?.down('xs')} {
      height: 400px;
    }
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default DriverTrackerMap;
